import React, { useMemo } from 'react';

import delv from 'dlv';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ILoyaltyCustomizableContent } from '@rbi-ctg/menu';
import CardDeactivationModal from 'components/card-deactivation-modal';
import { brandFont } from 'components/layout/brand-font';
import Picture from 'components/picture';
import BlockRenderer from 'components/sanity-block-renderer';
import TextButton from 'components/text-button';
import UserVerification from 'components/user-verification';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { LOYALTY_ERROR_PLACEHOLDER } from 'hooks/loyalty/constants';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useErrorModal from 'hooks/use-error-modal';
import useMediaQuery from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';
import useUserHasToVerify from 'hooks/use-user-has-to-verify';
import { useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants/primitives';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import Registration from './registration';
import RoundUpToggle from './round-up-toggle';

const Container = styled.div`
  max-width: ${Styles.layout.sectionMaxWidth};
  margin: 0 auto;
  padding: ${primitive.$spacing5};
  line-height: 1.4em;

  ${Styles.desktop`
    flex-direction: row;
  `};
`;

const PointDetails = styled.div`
  font-size: 1.5rem;
  border: 1px solid ${p => p.theme.token('border-color-default')};
  border-radius: 6px;
  padding: ${primitive.$spacing3} ${primitive.$spacing6};
  margin: -${primitive.$spacing7} auto 0;
  z-index: ${Styles.zIndex.below};
  background-color: ${p => p.theme.token('background-pattern')};
`;

const PointTotal = styled.span`
  font-weight: 800;
  color: ${p => p.theme.token('text-active')};
`;

const SubSection = styled.section`
  margin: ${primitive.$spacing7} 0;
  padding: 0 ${primitive.$spacing4};
  border-top: 1px solid ${p => p.theme.token('border-color-options')};
`;

const SubSectionTitle = styled.div`
  margin-top: ${primitive.$spacing7};
  margin-bottom: ${primitive.$spacing5};
  font: ${brandFont.headerTwo};
  text-align: center;
`;

const SubSectionContent = styled.div<{ centered?: boolean }>`
  font: ${brandFont.copyOne};
  margin: ${primitive.$spacing4};
  color: ${p => p.theme.token('text-default')};
  text-align: ${({ centered }) => (centered ? 'center' : 'left')};
`;

const TimsRewardsTitle = styled(SubSectionTitle)`
  height: 100px;
  margin-top: ${primitive.$spacing1};
  margin-bottom: ${primitive.$spacing7};
`;

const TimsRewardsIcon = styled(Picture)`
  margin-right: ${primitive.$spacing8};
  flex: 0 0 ${primitive.$spacing8};
`;

const TimsRewardsPoint = styled.div`
  color: ${p => p.theme.token('text-default')};
  flex: 1 1 240px;
`;

const TimsRewardsItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${primitive.$spacing5};
`;

const UserAccountNumberText = styled(SubSectionContent)`
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: ${p => p.theme.token('text-link-default')};
`;

const CardNumber = styled.p`
  font-weight: bold;
`;

export function generateCardIdLabel(cardId: string) {
  return cardId.length !== 12
    ? cardId
    : `${cardId.slice(0, 4)}-${cardId.slice(4, 8)}-${cardId.slice(8, 12)}`;
}
const HeroImageWrapper = styled.div`
  padding-bottom: ${primitive.$spacing5};
  background: ${p => p.theme.token('background-active')};
  height: calc(min(400px, 80vw));

  ${Styles.desktop`
    img {
      object-fit: contain;
    }
  `}
`;
const RoundUpContent = styled.div`
  margin-top: 1rem;
  margin-bottom: 3rem;
`;

export type IDetailsProps = {
  loyaltyContent: ILoyaltyCustomizableContent;
};

const Details = ({ loyaltyContent }: IDetailsProps) => {
  const { formatMessage } = useIntl();
  const { error: activeCardError, activeCard, loyaltyDetails } = useLoyaltyActiveCard();
  const enableVoucher = useFlag(LaunchDarklyFlag.ENABLE_TH_VOUCHER_CODES);
  const enableUnverifiedLoyaltyRegistration = useFlag(
    LaunchDarklyFlag.ENABLE_UNVERIFIED_LOYALTY_REGISTRATION
  );
  const enableRoundUpToggle = useFlag(LaunchDarklyFlag.ENABLE_TH_ROUND_UP);
  const userHasToVerify = useUserHasToVerify(enableUnverifiedLoyaltyRegistration);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Failed to get user loyalty',
  });
  const [CardDeactivationDialog, showCardDeactivationModal] = useDialogModal({
    Component: CardDeactivationModal,
  });
  const { getLocalizedRouteForPath } = useRoutes();

  const { cardId = '' } = activeCard || {};
  const userAccountNumber = useMemo(() => generateCardIdLabel(cardId), [cardId]);

  useEffectOnUpdates(() => {
    openErrorDialog({
      message: formatMessage({ id: 'loadLoyaltyError' }),
      error: activeCardError,
    });
  }, [activeCardError]);

  const isMobile = useMediaQuery('mobile');
  const detailsPageHeroImage = isMobile
    ? delv(loyaltyContent, 'detailsPageHeroImageMobile.locale')
    : delv(loyaltyContent, 'detailsPageHeroImageWeb.locale');
  const timsRewardsImage = delv(loyaltyContent, 'timsRewardsImage.locale');
  const detailsInstructionsOne = delv(loyaltyContent, 'detailsInstructionsOne.locale');
  const detailsInstructionsTwo = delv(loyaltyContent, 'detailsInstructionsTwo.locale');
  const detailsInstructionsThree = delv(loyaltyContent, 'detailsInstructionsThree.locale');
  const detailsInstructionsFour = delv(loyaltyContent, 'detailsInstructionsFour.locale');

  return (
    <>
      <HeroImageWrapper>
        {detailsPageHeroImage && (
          <Picture
            image={detailsPageHeroImage}
            alt={formatMessage({ id: 'loyaltyDetailsHeroImage' })}
          />
        )}
      </HeroImageWrapper>
      <PointDetails data-testid="rewards-points-detail-message">
        {formatMessage(
          { id: 'rewardsPointsDetailsMessage' },
          {
            points: (
              <PointTotal>
                {loyaltyDetails ? loyaltyDetails.points : LOYALTY_ERROR_PLACEHOLDER}
              </PointTotal>
            ),
          }
        )}
      </PointDetails>
      <Container>
        {enableRoundUpToggle && loyaltyDetails && (
          <RoundUpContent>
            <RoundUpToggle cardId={cardId} donationStatus={loyaltyDetails.donationUpcharge} />
          </RoundUpContent>
        )}
        <TimsRewardsTitle>
          {timsRewardsImage && (
            <Picture
              image={timsRewardsImage}
              alt={formatMessage({ id: 'timsRewards' })}
              objectFitContain
            />
          )}
        </TimsRewardsTitle>
        <SubSectionContent>
          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconOne.locale')}
              alt={detailsInstructionsOne}
            />
            <TimsRewardsPoint>{detailsInstructionsOne}</TimsRewardsPoint>
          </TimsRewardsItem>
          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconTwo.locale')}
              alt={detailsInstructionsTwo}
            />
            <TimsRewardsPoint>{detailsInstructionsTwo}</TimsRewardsPoint>
          </TimsRewardsItem>

          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconThree.locale')}
              alt={detailsInstructionsThree}
            />
            <TimsRewardsPoint>{detailsInstructionsThree}</TimsRewardsPoint>
          </TimsRewardsItem>

          <TimsRewardsItem>
            <TimsRewardsIcon
              image={delv(loyaltyContent, 'detailsIconFour.locale')}
              alt={detailsInstructionsFour}
            />
            <TimsRewardsPoint>{detailsInstructionsFour}</TimsRewardsPoint>
          </TimsRewardsItem>
          <StyledLink
            data-testid="terms-conditions-rewards-link"
            to={getLocalizedRouteForPath('terms-conditions-rewards')}
          >
            {formatMessage({ id: 'termsConditionsRewards' })}
          </StyledLink>
        </SubSectionContent>
        <SubSection>
          <SubSectionTitle>
            {delv(loyaltyContent, 'registerCardSectionTitle.locale')}
          </SubSectionTitle>
          <SubSectionContent>
            {userHasToVerify ? (
              <UserVerification />
            ) : (
              <>
                <BlockRenderer
                  content={delv(loyaltyContent, 'registerCardSectionBlockText.localeRaw')}
                />
                {activeCard && (
                  <CardNumber>
                    {formatMessage(
                      { id: 'yourCurrentTimsRewardCardNumberIs' },
                      {
                        userAccountNumber: generateCardIdLabel(activeCard.cardId),
                      }
                    )}
                  </CardNumber>
                )}
                <Registration activeCard={activeCard} enableVoucher={enableVoucher} />
              </>
            )}
          </SubSectionContent>
        </SubSection>
        {enableVoucher && (
          <SubSection>
            <SubSectionTitle>{formatMessage({ id: 'enterAPromoCode' })}</SubSectionTitle>
            <SubSectionContent>
              <p>{formatMessage({ id: 'promoCodeBodyDetails' })}</p>
              <Registration
                activeCard={activeCard}
                enableVoucher={enableVoucher}
                buttonText={formatMessage({ id: 'promoCodeSubmitBtnText' })}
                isVoucher
              />
            </SubSectionContent>
          </SubSection>
        )}
        <SubSection>
          <SubSectionTitle>
            {delv(loyaltyContent, 'loyaltyCardSectionTitle.locale')}
          </SubSectionTitle>
          <UserAccountNumberText>{userAccountNumber}</UserAccountNumberText>
        </SubSection>
        {!userHasToVerify && (
          <SubSection>
            <SubSectionTitle>{delv(loyaltyContent, 'lostCardSectionTitle.locale')}</SubSectionTitle>
            <SubSectionContent>
              <BlockRenderer content={delv(loyaltyContent, 'lostCardSectionBlockText.localeRaw')} />
            </SubSectionContent>
            <SubSectionContent centered>
              <TextButton data-testid="deactivate-card-link" onClick={showCardDeactivationModal}>
                {formatMessage({ id: 'deactivateCard' })}
              </TextButton>
            </SubSectionContent>
          </SubSection>
        )}
      </Container>
      <CardDeactivationDialog />
      <ErrorDialog />
    </>
  );
};

export default Details;
